<template>
  <transition name="toast-fade">
    <div
      v-if="visible"
      class="toast-container"
      :class="type"
      :style="{ '--toast-duration': `${duration}ms` }"
      :role="type === 'error' ? 'alert' : 'status'"
      :aria-live="type === 'error' ? 'assertive' : 'polite'"
    >
      <div class="toast-accent" aria-hidden="true"></div>

      <div class="toast-icon" aria-hidden="true">
        <svg v-if="type === 'success'" viewBox="0 0 24 24">
          <path d="m7.5 12.5 3 3 6.5-7" />
        </svg>
        <svg v-else-if="type === 'error'" viewBox="0 0 24 24">
          <path d="m8 8 8 8M16 8l-8 8" />
        </svg>
        <svg v-else-if="type === 'warning'" viewBox="0 0 24 24">
          <path d="M12 4.5 20 19H4L12 4.5Z" />
          <path d="M12 9v4.5M12 16.5v.1" />
        </svg>
        <svg v-else viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8" />
          <path d="M12 11v5M12 8v.1" />
        </svg>
      </div>

      <div class="toast-copy">
        <span class="toast-title">{{ typeTitle }}</span>
        <span class="toast-message">{{ message }}</span>
      </div>

      <button class="toast-close" type="button" aria-label="关闭提示" @click="hide">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="m8 8 8 8M16 8l-8 8" />
        </svg>
      </button>

      <div v-if="duration > 0" class="toast-progress" aria-hidden="true"></div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ToastComponent',
  props: {
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    duration: {
      type: Number,
      default: 3000
    }
  },
  emits: ['close'],
  data() {
    return {
      visible: false,
      hideTimer: null,
      closeTimer: null
    }
  },
  computed: {
    typeTitle() {
      return {
        success: '操作成功',
        error: '操作未完成',
        warning: '温馨提示',
        info: '系统提示'
      }[this.type]
    }
  },
  mounted() {
    this.show()
  },
  beforeUnmount() {
    window.clearTimeout(this.hideTimer)
    window.clearTimeout(this.closeTimer)
  },
  methods: {
    show() {
      this.visible = true
      if (this.duration > 0) {
        this.hideTimer = window.setTimeout(this.hide, this.duration)
      }
    },
    hide() {
      if (!this.visible) return

      window.clearTimeout(this.hideTimer)
      this.visible = false
      this.closeTimer = window.setTimeout(() => {
        this.$emit('close')
      }, 220)
    }
  }
}
</script>

<style scoped>
.toast-container {
  --toast-color: #4263eb;
  --toast-soft: rgba(66, 99, 235, 0.11);
  position: fixed;
  top: 20px;
  left: 50%;
  z-index: 13000;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 12px;
  width: min(390px, calc(100vw - 40px));
  min-height: 72px;
  padding: 13px 12px 13px 14px;
  overflow: hidden;
  color: #17213f;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(116, 133, 174, 0.32);
  border-radius: 18px;
  box-shadow:
    0 18px 45px rgba(45, 58, 103, 0.16),
    0 4px 12px rgba(45, 58, 103, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(22px) saturate(145%);
  -webkit-backdrop-filter: blur(22px) saturate(145%);
  transform: translateX(-50%);
  isolation: isolate;
}

.toast-container::before {
  position: absolute;
  inset: 0;
  z-index: -1;
  content: '';
  pointer-events: none;
  background:
    radial-gradient(circle at 12% 0%, var(--toast-soft), transparent 48%),
    linear-gradient(115deg, rgba(255, 255, 255, 0.58), transparent 58%);
}

.toast-container.success {
  --toast-color: #079c84;
  --toast-soft: rgba(7, 156, 132, 0.12);
}

.toast-container.error {
  --toast-color: #ef5261;
  --toast-soft: rgba(239, 82, 97, 0.12);
}

.toast-container.warning {
  --toast-color: #d88916;
  --toast-soft: rgba(216, 137, 22, 0.13);
}

.toast-accent {
  position: absolute;
  top: 15px;
  bottom: 15px;
  left: 0;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--toast-color);
}

.toast-icon {
  display: grid;
  width: 40px;
  height: 40px;
  color: var(--toast-color);
  place-items: center;
  background: var(--toast-soft);
  border: 1px solid color-mix(in srgb, var(--toast-color) 18%, transparent);
  border-radius: 13px;
}

.toast-icon svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.toast-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.toast-title {
  color: #17213f;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.toast-message {
  color: #697590;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.toast-close {
  display: grid;
  width: 28px;
  height: 28px;
  padding: 0;
  color: #8a94aa;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 9px;
  place-items: center;
  transition: color 0.2s ease, background 0.2s ease;
}

.toast-close:hover {
  color: #44506b;
  background: rgba(91, 105, 139, 0.09);
}

.toast-close:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--toast-color) 42%, transparent);
  outline-offset: 1px;
}

.toast-close svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-width: 1.8;
}

.toast-progress {
  position: absolute;
  right: 14px;
  bottom: 0;
  left: 14px;
  height: 2px;
  background: linear-gradient(90deg, var(--toast-color), color-mix(in srgb, var(--toast-color) 55%, #27baf0));
  border-radius: 2px 2px 0 0;
  transform-origin: left;
  animation: toast-progress var(--toast-duration) linear forwards;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate3d(-50%, -10px, 0) scale(0.98);
}

@keyframes toast-progress {
  to {
    transform: scaleX(0);
  }
}

@media (max-width: 600px) {
  .toast-container {
    top: 12px;
    right: 12px;
    left: 12px;
    width: auto;
    min-height: 66px;
    grid-template-columns: 36px minmax(0, 1fr) 26px;
    gap: 10px;
    padding: 11px 10px 11px 12px;
    border-radius: 16px;
    transform: none;
  }

  .toast-fade-enter-from,
  .toast-fade-leave-to {
    transform: translate3d(0, -10px, 0) scale(0.98);
  }

  .toast-icon {
    width: 36px;
    height: 36px;
    border-radius: 12px;
  }

  .toast-title {
    font-size: 13px;
  }

  .toast-message {
    font-size: 12.5px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .toast-fade-enter-active,
  .toast-fade-leave-active {
    transition-duration: 0.01ms;
  }

  .toast-progress {
    animation: none;
  }
}
</style>
